import { format } from "date-fns";
import { TFunction } from "i18next";
import { validateDate } from "../../../app/common";
import { SearchFilter } from "../../../common/components/functional/FilterSearchField/FilterSearchField";
import {
  makeLookupValues,
  makeMultiSelectDisplay,
} from "../../../common/helpers/filters";
import {
  isoFormat,
  shortMonthYearFormat,
  yearMonthFormat,
} from "../../../common/helpers/formatDateTime";
import { AutocompleteType } from "../../../common/types/autoComplete";
import { CustomerOrganization } from "../../../common/types/apiTypes";
import { EezyCompany, EezyCostCenter } from "../../invoices/invoiceAPI";
import { Talent } from "../../talents/talentAPI";
import { SCHEDULING_PERIOD_TYPES } from "../../settings/helpers/utils";

export type SalaryFilterFormTypes = {
  type: string;
  salaryDateFrom: Date | null;
  salaryDateTo: Date | null;
  valueDateFrom: Date | null;
  valueDateTo: Date | null;
  incomeNetFrom: string;
  incomeNetTo: string;
  eezyCompanies: AutocompleteType[];
  talent: Talent | null;
  ssn: string;
  // wage report status
  statues: string;
  // payslip status
  statusCodes: AutocompleteType[];
  // employer health insurance reduction filters
  employer: EezyCompany | null;
  employerCostCenter: EezyCostCenter | null;
  customerOrganization: CustomerOrganization | null;
  yearMonth: Date | null;
  // ids: "true" | "false"
  isManual: AutocompleteType | null;
  // salary and billing period (ids: SCHEDULING_PERIOD_TYPES)
  salaryPeriodClass: AutocompleteType | null;
  // payslip salary types (ids: HOURLY, MONTHLY)
  salaryTypes: AutocompleteType[];
};

export const DEFAULT_SALARY_FORM: SalaryFilterFormTypes = {
  type: "",
  salaryDateFrom: null,
  salaryDateTo: null,
  valueDateFrom: null,
  valueDateTo: null,
  incomeNetFrom: "",
  incomeNetTo: "",
  eezyCompanies: [],
  talent: null,
  ssn: "",
  statues: "",
  statusCodes: [],
  employer: {
    eezyCompanyId: "",
    name: "",
    businessId: "",
    isFranchise: false,
    costCenters: [],
  },
  employerCostCenter: { eezyCostCenterId: "", name: "", code: "" },
  customerOrganization: { id: "", name: "" },
  yearMonth: null,
  isManual: null,
  salaryPeriodClass: null,
  salaryTypes: [],
};

// Form values -> filters (chips + query params)
export const buildSalaryFilters = (
  values: SalaryFilterFormTypes,
  t: TFunction,
): SearchFilter[] => {
  const {
    type,
    salaryDateFrom,
    salaryDateTo,
    valueDateFrom,
    valueDateTo,
    incomeNetFrom,
    incomeNetTo,
    eezyCompanies,
    talent,
    ssn,
    statues,
    statusCodes,
    employer,
    employerCostCenter,
    customerOrganization,
    yearMonth,
    isManual,
    salaryPeriodClass,
    salaryTypes,
  } = values;

  const { displayValue: eezyCompaniesDisplay, tooltip: eezyCompaniesTooltip } =
    makeMultiSelectDisplay(eezyCompanies);
  const { displayValue: statusCodesDisplay, tooltip: statusCodesTooltip } =
    makeMultiSelectDisplay(statusCodes);
  const { displayValue: salaryTypesDisplay, tooltip: salaryTypesTooltip } =
    makeMultiSelectDisplay(salaryTypes);

  return [
    { name: "type", value: type },
    {
      name: "salaryDateFrom",
      value:
        salaryDateFrom && validateDate(salaryDateFrom, isoFormat)
          ? format(salaryDateFrom, isoFormat)
          : "",
      type: "date",
    },
    {
      name: "salaryDateTo",
      value:
        salaryDateTo && validateDate(salaryDateTo, isoFormat)
          ? format(salaryDateTo, isoFormat)
          : "",
      type: "date",
    },
    {
      name: "valueDateFrom",
      value:
        valueDateFrom && validateDate(valueDateFrom, isoFormat)
          ? format(valueDateFrom, isoFormat)
          : "",
      type: "date",
    },
    {
      name: "valueDateTo",
      value:
        valueDateTo && validateDate(valueDateTo, isoFormat)
          ? format(valueDateTo, isoFormat)
          : "",
      type: "date",
    },
    {
      name: "incomeNetFrom",
      value: incomeNetFrom ? incomeNetFrom : "",
      type: "number",
    },
    {
      name: "incomeNetTo",
      value: incomeNetTo ? incomeNetTo : "",
      type: "number",
    },
    {
      name: "eezyCompanyIds",
      value: eezyCompanies.map((company) => company.id),
      displayName: "employer",
      displayValue: eezyCompaniesDisplay,
      ...(eezyCompaniesTooltip && { tooltip: eezyCompaniesTooltip }),
      lookupValues: makeLookupValues(eezyCompanies),
    },
    {
      name: "talentIds",
      value: talent ? talent.id : "",
      displayName: "talent",
      displayValue: talent
        ? `${talent.account.firstName} ${talent.account.lastName}`
        : "",
    },
    {
      name: "ssn",
      value: ssn ? ssn : "",
      type: "number",
    },
    {
      name: "statusCodes",
      value: statusCodes.map((code) => code.id),
      displayName: "payslipStatus",
      displayValue: statusCodesDisplay,
      ...(statusCodesTooltip && { tooltip: statusCodesTooltip }),
      lookupValues: makeLookupValues(statusCodes),
    },
    {
      name: "statues",
      value: statues ? statues : "",
      displayValue: statues
        ? t("payslips:" + statues.charAt(0).toLowerCase() + statues.slice(1))
        : "",
    },
    {
      name: "employerId",
      value: employer?.eezyCompanyId || "",
      displayName: "employer",
      displayValue: employer?.name || "",
    },
    {
      name: "employerCostCenterId",
      value: employerCostCenter?.eezyCostCenterId || "",
      displayName: "employerCostCenter",
      displayValue: employerCostCenter?.name || "",
    },
    {
      name: "customerOrganizationId",
      value: customerOrganization?.id || "",
      displayName: "customerOrg",
      displayValue: customerOrganization?.name || "",
    },
    {
      name: "yearMonth",
      displayName: "time",
      value:
        yearMonth && validateDate(yearMonth, yearMonthFormat)
          ? format(yearMonth, yearMonthFormat)
          : "",
      displayValue: yearMonth ? format(yearMonth, shortMonthYearFormat) : "",
      type: "date",
    },
    {
      name: "isManual",
      value: isManual?.id || "",
      displayName: "manualPayslip",
      displayValue: isManual ? t(isManual.id === "true" ? "yes" : "no") : "",
    },
    {
      name: "salaryPeriodClassId",
      value: salaryPeriodClass?.id || "",
      displayName: "salaryAndBillingPeriod",
      displayValue: salaryPeriodClass
        ? `schedulingPeriods:${salaryPeriodClass.name}`
        : "",
    },
    {
      name: "salaryTypes",
      value: salaryTypes.map((salaryType) => salaryType.id),
      displayName: "salaryType",
      displayValue: salaryTypesDisplay,
      ...(salaryTypesTooltip && { tooltip: salaryTypesTooltip }),
      lookupValues: makeLookupValues(salaryTypes),
    },
  ];
};

// Ids stored in a filter value: array (current) or comma separated string (legacy), empty ids dropped
const filterIds = (filter: SearchFilter | undefined): string[] => {
  const value = filter?.value;
  if (!value) return [];
  const ids = Array.isArray(value) ? value : String(value).split(",");
  return ids.map(String).filter(Boolean);
};

// Resolves names for the ids: lookupValues first, legacy comma separated displayValue as a fallback
const parseIdNames = (
  filter: SearchFilter | undefined,
): { id: string; name: string }[] => {
  const legacyNames = filter?.lookupValues
    ? undefined
    : filter?.displayValue?.toString().split(",");
  return filterIds(filter).map((id, index) => ({
    id,
    name:
      filter?.lookupValues?.find((lookup) => lookup.id === id)?.name ||
      legacyNames?.[index] ||
      "",
  }));
};

const parseDate = (filter: SearchFilter | undefined): Date | null =>
  filter?.value ? new Date(filter.value) : null;

// Filters -> form values. Tolerates missing filters (e.g. stale localStorage).
export const parseSalaryFilters = (
  filters: SearchFilter[],
): SalaryFilterFormTypes => {
  const find = (name: string) => filters.find((f) => f.name === name);

  const talentFilter = find("talentIds");
  const talentNameParts = talentFilter?.displayValue?.toString().split(" ");
  const employerFilter = find("employerId");
  const costCenterFilter = find("employerCostCenterId");
  const customerOrgFilter = find("customerOrganizationId");
  const isManualFilter = find("isManual");
  const salaryPeriodClassFilter = find("salaryPeriodClassId");

  return {
    type: find("type")?.value ?? "",
    salaryDateFrom: parseDate(find("salaryDateFrom")),
    salaryDateTo: parseDate(find("salaryDateTo")),
    valueDateFrom: parseDate(find("valueDateFrom")),
    valueDateTo: parseDate(find("valueDateTo")),
    incomeNetFrom: find("incomeNetFrom")?.value?.toString() ?? "",
    incomeNetTo: find("incomeNetTo")?.value?.toString() ?? "",
    eezyCompanies: parseIdNames(find("eezyCompanyIds")),
    talent: talentFilter?.value
      ? ({
          id: talentFilter.value,
          account: {
            firstName: talentNameParts?.[0] ?? "",
            lastName: talentNameParts?.slice(1).join(" ") ?? "",
          },
        } as Talent)
      : null,
    ssn: find("ssn")?.value?.toString() ?? "",
    statues: find("statues")?.value ?? "",
    statusCodes: parseIdNames(find("statusCodes")),
    employer: {
      ...DEFAULT_SALARY_FORM.employer,
      eezyCompanyId: employerFilter?.value || "",
      name: employerFilter?.displayValue?.toString() || "",
    } as EezyCompany,
    employerCostCenter: {
      ...DEFAULT_SALARY_FORM.employerCostCenter,
      eezyCostCenterId: costCenterFilter?.value || "",
      name: costCenterFilter?.displayValue?.toString() || "",
    } as EezyCostCenter,
    customerOrganization: {
      id: customerOrgFilter?.value || "",
      name: customerOrgFilter?.displayValue?.toString() || "",
    },
    yearMonth: parseDate(find("yearMonth")),
    isManual: isManualFilter?.value
      ? {
          // matches trueFalseAutocompleteOptions
          id: isManualFilter.value.toString(),
          name: isManualFilter.value.toString() === "true" ? "yes" : "no",
        }
      : null,
    salaryPeriodClass:
      SCHEDULING_PERIOD_TYPES.find(
        (period) => period.id === salaryPeriodClassFilter?.value,
      ) || null,
    salaryTypes: parseIdNames(find("salaryTypes")),
  };
};
